
import { ResponsiveCalendar } from '@nivo/calendar';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import useAnalyticsData from '../hooks/useAnalyticsData';
import useTodos from '../hooks/useTodos';
import React, { useState } from 'react';

import './analytics.css';

function toCSV(arr) {
  if (!arr || arr.length === 0) return '';
  const keys = Object.keys(arr[0]);
  const lines = [keys.join(',')];
  arr.forEach(obj => {
    lines.push(keys.map(k => JSON.stringify(obj[k] ?? '')).join(','));
  });
  return lines.join('\n');
}

function downloadFile(filename, content, type = 'text/plain') {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}

// ...existing imports...

export default function AnalyticsPage() {
  const {
    loading,
    calendarData,
    focusBarData,
    priorityBarData,
    donutData,
    leaderboardData,
    procrastinatedData,
    sessionDetails,
    summaryData,
  } = useAnalyticsData();
  const { todos } = useTodos();
  const [tab, setTab] = useState('today');
  // Calendar range
  const calendarFrom = calendarData.length > 0 ? calendarData[0].day : '2025-10-01';
  const calendarTo = calendarData.length > 0 ? calendarData[calendarData.length - 1].day : '2025-10-31';

  // Tab data selection
  let summaryTabData = [];
  if (tab === 'today') summaryTabData = summaryData?.daily || [];
  else if (tab === 'week') summaryTabData = summaryData?.weekly || [];
  else if (tab === 'month') summaryTabData = summaryData?.monthly || [];

  return (
    <div className="analytics-page">
      <header className="analytics-header">
        <h2>Analytics</h2>
        <p className="subtitle">Track your focus, streaks, and progress.</p>
      </header>
      <main className="analytics-main">
        {/* Tabs: Today, Week, Month */}
        <nav className="analytics-tabs" aria-label="Analytics Tabs">
          <button className={`tab${tab === 'today' ? ' active' : ''}`} onClick={() => setTab('today')}>Today</button>
          <button className={`tab${tab === 'week' ? ' active' : ''}`} onClick={() => setTab('week')}>Week</button>
          <button className={`tab${tab === 'month' ? ' active' : ''}`} onClick={() => setTab('month')}>Month</button>
        </nav>
        <section className="analytics-section">
          {/* Streak Calendar (Heatmap) */}
          <div style={{ height: 220, marginBottom: 12, background: '#0b1220', borderRadius: 10, padding: 8, minWidth: 0 }}>
            <ResponsiveCalendar
              data={calendarData}
              from={calendarFrom}
              to={calendarTo}
              emptyColor="#222"
              colors={["#fb6087", "#fbbf24", "#34d399", "#60a5fa"]}
              minValue={0}
              maxValue={100}
              margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
              yearSpacing={40}
              monthBorderColor="#374151"
              dayBorderWidth={2}
              dayBorderColor="#374151"
            />
          </div>

          {/* Focus Minutes & Pomodoros (Bar/Line) */}
          <div style={{ height: 220, marginBottom: 12, background: '#0b1220', borderRadius: 10, padding: 8, minWidth: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={focusBarData}>
                <XAxis dataKey="day" stroke="#e5e7eb" />
                <YAxis stroke="#e5e7eb" />
                <Tooltip />
                <Legend />
                <Bar dataKey="focus" fill="#fb6087" name="Focus Minutes" />
                <Bar dataKey="pomos" fill="#60a5fa" name="Pomodoros" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Pomodoros by Tag/Priority (Stacked Bar) */}
          {priorityBarData.length === 0 ? (
            <div style={{ color: '#ef4444', textAlign: 'center', padding: '32px 0' }}>No data found for this chart.</div>
          ) : (
            <div style={{ height: 220, marginBottom: 12, background: '#0b1220', borderRadius: 10, padding: 8, minWidth: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={priorityBarData}>
                  <XAxis dataKey="day" stroke="#e5e7eb" />
                  <YAxis stroke="#e5e7eb" />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="P1" stackId="a" fill="#fb6087" name="P1" />
                  <Bar dataKey="P2" stackId="a" fill="#fbbf24" name="P2" />
                  <Bar dataKey="P3" stackId="a" fill="#34d399" name="P3" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Estimate vs Actual (Donut) */}
          {donutData.length === 0 ? (
            <div style={{ color: '#ef4444', textAlign: 'center', padding: '32px 0' }}>No data found for this chart.</div>
          ) : (
            <div style={{ height: 220, marginBottom: 12, background: '#0b1220', borderRadius: 10, padding: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={donutData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    fill="#fb6087"
                    label
                  >
                    <Cell key="est" fill="#fbbf24" />
                    <Cell key="act" fill="#60a5fa" />
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Leaderboard & Garden Growth (Bar) */}
          {leaderboardData.length === 0 ? (
            <div style={{ color: '#ef4444', textAlign: 'center', padding: '32px 0' }}>No data found for this chart.</div>
          ) : (
            <div style={{ height: 120, marginBottom: 12, background: '#0b1220', borderRadius: 10, padding: 8, minWidth: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={leaderboardData}>
                  <XAxis dataKey="name" stroke="#e5e7eb" />
                  <YAxis stroke="#e5e7eb" />
                  <Tooltip />
                  <Bar dataKey="value" fill="#fb6087" name="Focus Minutes" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Procrastinated Time (Negative Metric) */}
          {procrastinatedData.length === 0 ? (
            <div style={{ color: '#ef4444', textAlign: 'center', padding: '32px 0' }}>No data found for this chart.</div>
          ) : (
            <div style={{ height: 120, marginBottom: 12, background: '#0b1220', borderRadius: 10, padding: 8, minWidth: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={procrastinatedData}>
                  <XAxis dataKey="name" stroke="#e5e7eb" />
                  <YAxis stroke="#e5e7eb" />
                  <Tooltip />
                  <Bar dataKey="value" fill="#ef4444" name="Procrastinated Time" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>
        {/* Daily/Weekly/Monthly Summaries (Line/Area) */}
        <section className="analytics-section" style={{ marginTop: 24 }}>
          <h3>Focus & Break Summaries</h3>
          {summaryTabData.length === 0 ? (
            <div style={{ color: '#ef4444', textAlign: 'center', padding: '32px 0' }}>No data found for this period.</div>
          ) : (
            <div style={{ height: 220, marginBottom: 12, background: '#0b1220', borderRadius: 10, padding: 8, minWidth: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={summaryTabData}>
                  <XAxis dataKey={tab === 'today' ? 'day' : tab === 'week' ? 'week' : 'month'} stroke="#e5e7eb" />
                  <YAxis stroke="#e5e7eb" />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="focus" fill="#fb6087" name="Focus Minutes" />
                  <Bar dataKey="break" fill="#60a5fa" name="Break Minutes" />
                  <Bar dataKey="todos" fill="#fbbf24" name={`Todos per ${tab.charAt(0).toUpperCase() + tab.slice(1)}`} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>
        {/* Session Details Table */}
        <section className="analytics-section" style={{ marginTop: 24 }}>
          <h3>Session Details</h3>
          <div style={{ overflowX: 'auto', background: '#0b1220', borderRadius: 10, padding: 8 }}>
            <table style={{ width: '100%', color: '#e5e7eb', fontSize: 14, borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ padding: '6px 12px' }}>Time</th>
                  <th style={{ padding: '6px 12px' }}>Mode</th>
                  <th style={{ padding: '6px 12px' }}>Pauses</th>
                  <th style={{ padding: '6px 12px' }}>Interruptions</th>
                  <th style={{ padding: '6px 12px' }}>Attached Todos</th>
                  <th style={{ padding: '6px 12px' }}>Completed Checklist</th>
                </tr>
              </thead>
              <tbody>
                {sessionDetails && sessionDetails.length > 0 ? sessionDetails.map((s, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #374151' }}>
                    <td style={{ padding: '6px 12px' }}>{s.at ? new Date(s.at).toLocaleString() : '-'}</td>
                    <td style={{ padding: '6px 12px' }}>{s.mode || '-'}</td>
                    <td style={{ padding: '6px 12px' }}>{s.pauses && s.pauses.length > 0 ? s.pauses.length : 0}</td>
                    <td style={{ padding: '6px 12px' }}>{s.interruptions && s.interruptions.length > 0 ? s.interruptions.length : 0}</td>
                    <td style={{ padding: '6px 12px' }}>{s.attachedTodoIds && s.attachedTodoIds.length > 0 ? s.attachedTodoIds.join(', ') : '-'}</td>
                    <td style={{ padding: '6px 12px' }}>{s.completedChecklist && s.completedChecklist.length > 0 ? s.completedChecklist.length : 0}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: '12px' }}>No session data yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          <button className="primary" onClick={() => downloadFile('sessions.json', JSON.stringify(sessionDetails, null, 2), 'application/json')}>Export Sessions (JSON)</button>
          <button className="primary" onClick={() => downloadFile('sessions.csv', toCSV(sessionDetails), 'text/csv')}>Export Sessions (CSV)</button>
          <button className="primary" onClick={() => downloadFile('todos.json', JSON.stringify(todos, null, 2), 'application/json')}>Export Todos (JSON)</button>
          <button className="primary" onClick={() => downloadFile('todos.csv', toCSV(todos), 'text/csv')}>Export Todos (CSV)</button>
          <button className="primary" onClick={() => downloadFile('analytics.json', JSON.stringify({ calendarData, focusBarData, priorityBarData, donutData, leaderboardData, procrastinatedData, summaryData }, null, 2), 'application/json')}>Export Analytics (JSON)</button>
        </div>
      </main>
    </div>
  );
}